import toInteger from 'lodash/toInteger'
import { equals, isNil } from 'ramda'
import { forwardRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Avatar } from '@/components/avatar'
import FormatProfession from '@/components/format-profession'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize'
import VerifiedBadge from '@/components/verified-badge'
import UnverifiedBadge from '@/components/unverified-badge'
import useBasic from '@/hooks/use-basic'
import useMediaQuery from '@/hooks/use-media-query'
import { logUserView } from '@/ports'
import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'
import formatName from '@/utils/format-name'
import { getImageUrl } from '@/utils/image'
import toBoolean from '@/utils/to-boolean'
import { isNullOrEmpty } from '@/utils'
import { parsePhone } from '@/utils/format-phone'
import { formatSalaryRange } from '@/utils/format-salary'
import { PhoneNumber } from '@/components/phone-number'
import useRole from '@/hooks/use-role'
import useTenantProfileContext from './tenant-profile-context'

const Wrapper = styled.div`
  background: ${colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  max-height: 98vh;
  max-width: 767px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-height: unset;
    margin-top: 68px;
    border-radius: 16px 16px 0 0;
  }
`

const ModalClose = styled.div`
  position: absolute;
  top: 19px;
  right: 25px;
  z-index: 1;

  svg {
    cursor: pointer;
    margin: 0;
  }

  :hover {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    top: 20px;
    right: 20px;
  }
`

const ScrollWrapper = styled.div`
  background: ${colors.white};
  height: calc(100% - 40px);
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0px 24px 35px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: ${props => (props.isLandlord ? 'calc(100% - 210px)' : 'calc(100% - 160px)')};
    padding: 0px 24px 0px;
    flex-direction: column;
    align-items: center;
    overflow-y: scroll;

    ${props =>
      props.buttonsDisabled &&
      `
        height: calc(100vh - 160px);
        padding-bottom: 100px;
    `}
  }
`

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.grey};
  width: 282px;
  border-radius: 6px;
  height: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    max-width: 460px;
  }
`

const AvatarWrapper = styled.div`
  height: 280px;
  width: 280px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
    div > img {
      object-position: top;
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 20px 16px 10px;
    overflow: hidden;
  }
`

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Name = styled.h2`
  font-size: 25px;
  color: ${colors.regular};
  font-weight: ${fontWeights.bold};
  letter-spacing: 0.47px;
  line-height: 34px;
  padding: 0;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SearchPreferencesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const SearchPreference = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin: 0 10px 10px 0;
  height: 24px;
  padding: 8px 10px;
  font-size: 14px;
  color: ${colors.darkGrey};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.32px;
  line-height: 22px;
  background-color: ${colors.grey96};
`

const AboutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 40px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 20px;
    width: 100%;
    max-width: 460px;
    margin: 20px 0 40px;
  }
`

const SectionTitle = styled.h3`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};

  :not(:first-child) {
    margin-top: 20px;
  }
`

const ProfileTitle = styled.h3`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  border-bottom: 1px solid ${colors.grey};
  margin: 10px 0 20px;
  padding-bottom: 20px;
  padding-left: 24px;

  :not(:first-child) {
    margin-top: 28px;
  }
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  :not(:last-child) {
    border-bottom: 1px solid ${colors.grey};
  }
`

const Label = styled.p`
  width: 190px;

  display: flex;
  align-items: center;
  color: ${colors.mediumGrey};
  overflow-wrap: break-word;
  word-break: break-word;
  flex-shrink: 0;
  margin: 15px 0;
  margin-right: 15px;

  svg {
    margin-right: 10px;
    opacity: 0.8;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 160px;
  }
`

const Value = styled(Label)`
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  color: ${colors.regular};
  font-weight: ${fontWeights.medium};
  margin: 15px 0 16px;
  flex-shrink: 1;
`

const ApplyContainer = styled.div`
  width: 100%;
  height: 72px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px;
  background-color: ${colors.grey96};
  border-top: 2px solid rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    position: sticky;
    bottom: 0;
    justify-content: center;
  }
`

const ApplyButton = styled.button`
  width: 120px;
  height: 40px;
  border: none;
  background-color: ${props => (props.hasApplied ? colors.white : colors.blue16)};
  color: ${props => (props.hasApplied ? colors.blue16 : colors.white)};
  font-size: 12px;
  font-weight: ${fontWeights.bold};
  box-shadow: 0px 2px 4px rgba(19, 43, 62, 0.25), 0px 6px 6px 2px rgba(19, 43, 62, 0.1);
  border-radius: 6px;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const ButtonsWrapper = styled.div`
  width: 100%;
  height: 72px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px;
  gap: 10px;
  background-color: ${colors.grey96};
  border-top: 2px solid rgba(0, 0, 0, 0.1);

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    position: sticky;
    bottom: 0;
    justify-content: center;
  }
`

const StyledButton = styled.button`
  width: 120px;
  height: 40px;
  border: none;
  background-color: ${props => (props.isMaybeNot ? colors.white : colors.blue16)};
  color: ${props => (props.isMaybeNot ? colors.blue16 : colors.white)};
  font-size: 12px;
  font-weight: ${fontWeights.bold};
  box-shadow: 0px 2px 4px rgba(19, 43, 62, 0.25), 0px 6px 6px 2px rgba(19, 43, 62, 0.1);
  border-radius: 6px;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const DetailWrapper = styled.div`
  transition: opacity 0.2s ease-in-out;
  word-break: break-word;
  word-wrap: break-word;
  cursor: pointer;
  :hover {
    opacity: 0.7;
  }
  > a {
    color: ${colors.grey48};
    text-decoration: none;
  }
`

const InfoContainer = styled.div`
  margin-top: 20px;
`

const UnverifiedWrapper = styled.div`
  margin-left: 5px;
`

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.grey};
  margin: 20px 0;
`

const equalsOne = equals('1')

const getLanguages = (languages, spokenLanguages) => {
  const filteredLanguages = languages.filter(lang => spokenLanguages.indexOf(lang.txt_id) >= 0)
  return filteredLanguages.map(lang => lang.name).join(', ')
}

const TenantProfile = forwardRef(
  (
    {
      data,
      handleClickChat,
      handleRemoveTenant,
      inviteToApply,
      applicationId,
      history,
      buttonsDisabled = false,
      showInviteToApply,
      showViewApplication,
      timelineId
    },
    ref
  ) => {
    const { close } = useModalize()
    const isLandlord = useRole() === 'landlord'
    const { t } = useTranslation()
    const { languages } = useBasic(['languages'])
    const tenantProfileContext = useTenantProfileContext()
    const allowCalls = tenantProfileContext?.allowCalls
    const allowCallsIsntPresent = isNil(allowCalls)
    const hidePhone = allowCallsIsntPresent ? data?.hide_phone_number : !allowCalls
    const hideEmail = !data?.username
    const preferences = data.user_search_preferences || data.user_search_preference
    const showSeparator = preferences && (!hidePhone || !hideEmail)
    const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
    const tenantName = formatName(data)
    const isVerified =
      data.identity_verified_state === 'verified' || data.identity_verified_state === true

    useEffect(() => {
      logUserView(undefined, { userId: data.id })
    }, [data])

    const toFurnished = isFurnished => {
      if (toBoolean(isFurnished)) {
        return t('common.furnished', 'Furnished')
      }
      if (isFurnished === null) {
        return t('common.furnished_unfurnished', 'Furnished / Unfurnished')
      }
      return t('common.unfurnished', 'Unfurnished')
    }
    const formatSearchPreferences = ({ min_price, max_price, furnished }) => {
      const allPreferences = []
      const minPrice = toInteger(min_price)
      const maxPrice = toInteger(max_price)

      if (minPrice === 0 && maxPrice === 0) {
        allPreferences.push(
          <SearchPreference key="anyPrice">
            {t('l.profile.view.any_price.text', 'Any price')}
          </SearchPreference>
        )
      }

      if (minPrice > 0) {
        allPreferences.push(
          <SearchPreference key="minPrice">
            {`${t('l.profile.view.min_budget.text', 'Min. budget')} $${min_price}`}
          </SearchPreference>
        )
      }

      if (maxPrice > 0) {
        allPreferences.push(
          <SearchPreference key="maxPrice">
            {`${t('l.profile.view.max_budget.text', 'Max. budget')} $${max_price}`}
          </SearchPreference>
        )
      }

      allPreferences.push(
        <SearchPreference key="furnished">{toFurnished(furnished)}</SearchPreference>
      )

      return allPreferences
    }

    const getPetsAllowed = () => {
      // handle old user default value of null
      if (isNullOrEmpty(data.pet_owner)) return '-'
      // handle new user default value of '1'
      if (equalsOne(data.pet_owner)) {
        return isNullOrEmpty(data.user_pets)
          ? '-'
          : t('l.profile.view.have_pets.label', 'Has Pet(s)')
      }
      return t('l.profile.view.not_have_pets.label', 'No Pets')
    }

    const handleButtonClick = () => {
      if (showViewApplication) {
        history.push(`/chat/${timelineId}/application/${applicationId}`)
        close()
      } else if (showInviteToApply) {
        inviteToApply()
        close()
      }
    }

    const clickedMaybeNotButton = e => {
      handleRemoveTenant(e)
      close()
    }

    const clickedChatButton = () => {
      close()
      handleClickChat()
    }

    const renderFooter = () => {
      if (handleClickChat && handleRemoveTenant) {
        return (
          <ButtonsWrapper>
            <StyledButton isMaybeNot onClick={clickedMaybeNotButton}>
              {t('b.profile.view.suggested_tenant_dismiss.button', 'Maybe Not')}
            </StyledButton>
            <StyledButton onClick={clickedChatButton}>{t('common.chat', 'Chat')}</StyledButton>
          </ButtonsWrapper>
        )
      }

      if (!buttonsDisabled && isLandlord) {
        return (
          <ApplyContainer>
            <ApplyButton onClick={handleButtonClick} hasApplied={showViewApplication}>
              {showViewApplication
                ? t('l.chat.view.view_application.button', 'View Application')
                : t('l.chat.view.invite_to_apply.button', 'Invite to apply')}
            </ApplyButton>
          </ApplyContainer>
        )
      }

      return null
    }

    return (
      <Wrapper ref={ref}>
        <ModalClose>
          <Icon aria-label="Close" id="close" width={24} height={24} onClick={close} />
        </ModalClose>
        <ProfileTitle>{t('l.profile.view.profile.subtitle', 'Profile')}</ProfileTitle>
        <ScrollWrapper isLandlord={isLandlord} buttonsDisabled={buttonsDisabled}>
          <ProfileWrapper>
            <AvatarWrapper>
              <Avatar
                shadow={false}
                absolute={false}
                location={getImageUrl(data.avatar?.aws_s3_key, {
                  width: 280,
                  height: 280
                })}
                height={280}
                width={displayMobile ? '100%' : 280}
                radius={'6px 6px 0 0'}
              />
            </AvatarWrapper>
            <ContentWrapper>
              <NameWrapper>
                <Name>{tenantName}</Name>
                {isVerified ? (
                  <VerifiedBadge
                    type="id"
                    width={25}
                    height={25}
                    bottom={-3}
                    verified={data.identity_verified_state}
                  />
                ) : (
                  <UnverifiedWrapper>
                    <UnverifiedBadge />
                  </UnverifiedWrapper>
                )}
              </NameWrapper>
              {(!hidePhone || !hideEmail || preferences) && (
                <InfoContainer>
                  {!hidePhone && (
                    <DetailWrapper>
                      <PhoneNumber phoneNumber={parsePhone(data.phone_number)} />
                    </DetailWrapper>
                  )}
                  {!hideEmail && (
                    <DetailWrapper>
                      <a href={`mailto: ${data.username}`}>{data.username}</a>
                    </DetailWrapper>
                  )}
                  {showSeparator && <Separator />}
                  {preferences && (
                    <SearchPreferencesWrapper>
                      {formatSearchPreferences(preferences)}
                    </SearchPreferencesWrapper>
                  )}
                </InfoContainer>
              )}
            </ContentWrapper>
          </ProfileWrapper>
          <AboutWrapper>
            <SectionTitle>{t('l.profile.view.basic_info.subtitle', 'Basic Info')}</SectionTitle>
            <Row>
              <Label>{t('l.profile.view.languages_spoken.label', 'Languages')}</Label>
              <Value>
                {data.spoken_languages && !!data.spoken_languages.length
                  ? getLanguages(languages, data.spoken_languages)
                  : '-'}
              </Value>
            </Row>
            <Row>
              <Label>{t('l.profile.view.i_do.label', 'Pets')}</Label>
              <Value>{getPetsAllowed()}</Value>
            </Row>
            <Row>
              <Label>{t('l.profile.view.story.label', 'Story')}</Label>
              <Value>{isNullOrEmpty(data.about_me) ? '-' : data.about_me}</Value>
            </Row>

            <SectionTitle>{t('b.profile.general.job_title.text', 'Job Title')}</SectionTitle>
            <Row>
              <Label>{t('l.profile.view.occupation.label', 'Occupation')}</Label>
              <Value>
                {isNullOrEmpty(data.job_title) ? (
                  '-'
                ) : (
                  <FormatProfession profession={data.job_title} />
                )}
              </Value>
            </Row>
            <Row>
              <Label>{t('l.profile.view.income.label', 'Approx. annual income')}</Label>
              <Value>
                {isNullOrEmpty(data.salary_range) ? '-' : formatSalaryRange(data.salary_range, t)}
              </Value>
            </Row>
          </AboutWrapper>
        </ScrollWrapper>
        {renderFooter()}
      </Wrapper>
    )
  }
)

TenantProfile.displayName = 'TenantProfile'

export default TenantProfile

export const showTenantProfile = createModal(TenantProfile, {
  width: 'auto',
  maxWidth: '767px',
  tabletFullscreen: true,
  alignSelf: 'flex-start',
  showBackground: false,
  disableOverflowMobile: true
})
